import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import invariant from 'tiny-invariant'
import { match, P } from 'ts-pattern'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { ErrorBoundaryFallback } from '../../components/error-boundaries/error-boundary-fallback.tsx'
import { FreeformFinishedView } from '../../components/freeform/run/finished/freeform-finished-view.tsx'
import {
  failureReasonOf,
  type FreeformRunState,
  hasProgress,
  hasWholeHistory,
} from '../../components/freeform/run/freeform-run.ts'
import { FreeformLiveView } from '../../components/freeform/run/live/freeform-live-view.tsx'
import { useFreeformRun } from '../../components/freeform/run/use-freeform-run.ts'
import { Spinner } from '../../components/spinner.tsx'
import { useNow } from '../../hooks/use-now.ts'
import { useRereadUntilSettled } from '../../hooks/use-reread-until-settled.ts'
import { Routes } from '../../routes.ts'
import {
  FREEFORM_RUNNING_SESSION_POLL_MS,
  invalidateFreeformUploadSessions,
  invalidateGetScans,
  isFreeformUploadSessionNotFound,
  isFreeformUploadSessionStalled,
  isFreeformUploadSessionUnfinished,
  useGetFreeformUploadSession,
} from '../../utils/api-client/api-hooks.ts'
import type { FreeformUploadSessionResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './freeform-run-page.css.ts'

/**
 * One Findings Intake run, from the moment its files are uploaded: the live view while Pixee reads the
 * report and places each finding, and where it ended once it has.
 */
export const FreeformRunPage: React.FC = () => {
  const { sessionId } = useParams()
  invariant(sessionId, 'FreeformRunPage needs a sessionId route parameter')
  const { data: session, isLoading, error, refetch } = useGetFreeformUploadSession({ sessionId })

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading Findings Intake run" />
      </div>
    )
  }

  if (!session) {
    return (
      <div className={styles.page}>
        {error && !isFreeformUploadSessionNotFound(error) ? (
          <ErrorBoundaryFallback
            title="Unable to load Findings Intake run"
            detail={<ErrorDetail error={error} />}
            onRetry={() => refetch()}
          />
        ) : (
          <>
            <h1 className={styles.title}>Findings Intake run not found</h1>
            <Link to={Routes.OrganizationPage.createPath({ tab: 'freeform' })}>Back to Findings intake</Link>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Breadcrumb
        navAriaLabel="Findings Intake run breadcrumb"
        items={[
          { label: 'Findings intake', to: Routes.OrganizationPage.createPath({ tab: 'freeform' }) },
          { label: 'Run', to: Routes.FreeformRunPage.createPath({ sessionId }) },
        ]}
      />
      <RunHeader sessionId={sessionId} />
      <FreeformRun sessionId={sessionId} />
    </div>
  )
}

const RunHeader: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { data: session } = useGetFreeformUploadSession({ sessionId })
  if (!session) {
    return null
  }
  const [first, ...others] = session.files.map(file => file.filename)
  return (
    <header className={styles.header}>
      <p className={styles.eyebrow}>Findings Intake run</p>
      <h1 className={styles.title}>
        {first ?? 'Upload'}
        {others.length > 0 ? ` and ${others.length} more ${pluralize(others.length, 'file', 'files')}` : ''}
      </h1>
      <dl className={styles.facts}>
        {session.created_by && <Fact label="Uploaded by" value={session.created_by} />}
        <Fact label="Started" value={formatDate({ date: session.created_at, type: 'mm/dd/yy-time-with-zone' })} />
      </dl>
    </header>
  )
}

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={styles.fact}>
    <dt>{label}</dt>
    <dd className={styles.factValue}>{value}</dd>
  </div>
)

/**
 * How long a finished session's open stream may stay silent before the page stops waiting for its history:
 * the platform replays it at once when it still has it, and otherwise sends only keep-alives.
 */
const FREEFORM_HISTORY_WAIT_MS = 2_000

/** How often the page checks whether an unfinished run has gone quiet past its deadline. */
const FREEFORM_STALL_CHECK_MS = 60_000

/** True once `ms` have passed since `isStarted` first became true. */
const useHasWaited = (ms: number, isStarted: boolean) => {
  const [hasWaited, setHasWaited] = useState(false)
  useEffect(() => {
    if (!isStarted) {
      return
    }
    const timer = setTimeout(() => setHasWaited(true), ms)
    return () => clearTimeout(timer)
  }, [ms, isStarted])
  return hasWaited
}

const STALLED_REASON =
  'Pixee stopped hearing from this run, so it may have been interrupted. Upload the files again to retry.'

/**
 * The run as the live view should show it. The stream normally says how the run ended, but it can close
 * before its terminal event (the platform's stream deadline, a deploy, a proxy); the platform's record of
 * the session then says how it ended, and the scans it created.
 */
const runAsRecorded = (
  run: FreeformRunState,
  session: FreeformUploadSessionResponse,
  isStalled: boolean
): FreeformRunState =>
  match({ run, status: session.status, isStalled })
    .with({ run: { isDone: true } }, () => run)
    .with({ status: P.union('completed', 'failed') }, ({ status }) => ({
      ...run,
      isDone: true,
      phase: status,
      errorReason: status === 'failed' ? failureReasonOf(session) : undefined,
      repoScans:
        run.repoScans.length > 0
          ? run.repoScans
          : session.scans.map(scan => ({
              repoFullName: scan.repo_full_name,
              scanId: scan.scan_id,
              findingCount: scan.finding_count,
            })),
    }))
    .with({ isStalled: true }, () => ({ ...run, isDone: true, phase: 'failed' as const, errorReason: STALLED_REASON }))
    .otherwise(() => run)

/**
 * The run's live view, from upload to the end, and still there once it has ended: it holds where the run
 * finished, with a link to each scan. One view throughout, so the end of a run never re-mounts the page.
 * A finished session whose stream no longer holds its whole history shows its stored outcome instead.
 */
const FreeformRun: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const queryClient = useQueryClient()
  const { data: session } = useGetFreeformUploadSession({ sessionId })
  const { run: streamed, settledSeq, connectionStatus } = useFreeformRun({ sessionId })
  const isUnfinished = isFreeformUploadSessionUnfinished(session)
  const now = useNow({ intervalMs: FREEFORM_STALL_CHECK_MS, isTicking: isUnfinished })
  const isStalled = isFreeformUploadSessionStalled(session, now)
  const hasWaited = useHasWaited(FREEFORM_HISTORY_WAIT_MS, connectionStatus === 'connected')

  // The stream's terminal event can reach the page before the platform records the session as finished.
  useRereadUntilSettled({
    isSettling: streamed.isDone && isUnfinished,
    onSettle: () => {
      invalidateFreeformUploadSessions(queryClient)
      invalidateGetScans(queryClient)
    },
    reread: () => invalidateFreeformUploadSessions(queryClient),
    maxMs: FREEFORM_RUNNING_SESSION_POLL_MS,
  })

  if (!session) {
    return null
  }
  if (isUnfinished || (hasProgress(streamed) && hasWholeHistory(streamed))) {
    return <FreeformLiveView run={runAsRecorded(streamed, session, isStalled)} settledSeq={settledSeq} />
  }
  const isHistoryGone =
    hasProgress(streamed) || hasWaited || connectionStatus === 'finished' || connectionStatus === 'error'
  return isHistoryGone ? (
    <FreeformFinishedView sessionId={sessionId} run={streamed} />
  ) : (
    <div className={styles.loadingState}>
      <Spinner label="Loading Findings Intake run" />
    </div>
  )
}
